import React, { useState, useEffect } from "react";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Settings,
  Save,
  X,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

// Reusable Dialog Component for Category Form
const CategoryFormDialog = ({ open, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState({ name: "", description: "" });

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    }
  }, [initialData]);

  const handleSubmit = () => {
    onSubmit(form);
    setForm({ name: "", description: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Upraviť kategóriu" : "Pridať kategóriu"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Názov</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label>Popis</Label>
            <Input
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Zrušiť
          </Button>
          <Button onClick={handleSubmit} disabled={!form.name.trim()}>
            {initialData ? "Uložiť" : "Pridať"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Options Management Component
const OptionsManagement = ({ category, onUpdate, onClose }) => {
  const supabase = useSupabase();
  const [optionValue, setOptionValue] = useState("");
  const [editingOption, setEditingOption] = useState(null);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const [error, setError] = useState(null);

  const handleOptionAction = async (action, optionData = null) => {
    try {
      let result;
      switch (action) {
        case "add":
          result = await supabase
            .from("custom_category_options")
            .insert([
              {
                category_id: category.id,
                value: optionValue.trim(),
              },
            ])
            .select()
            .single();
          break;

        case "edit":
          result = await supabase
            .from("custom_category_options")
            .update({ value: optionValue.trim() })
            .eq("id", editingOption.id)
            .select()
            .single();
          break;

        case "delete":
          await supabase
            .from("custom_category_options")
            .delete()
            .eq("id", optionData.id);

          // Update local state immediately after successful deletion
          const updatedOptions = category.custom_category_options.filter(
            (opt) => opt.id !== optionData.id
          );

          // Update parent component with fresh data
          onUpdate({
            ...category,
            custom_category_options: updatedOptions,
          });

          // Reset states
          setOptionToDelete(null);
          return; // Exit early for delete case
      }

      if (result.error) throw result.error;

      // For add and edit cases:
      const updatedOptions =
        action === "add"
          ? [...category.custom_category_options, result.data]
          : category.custom_category_options.map((opt) =>
              opt.id === result.data.id ? result.data : opt
            );

      // Update parent component
      onUpdate({
        ...category,
        custom_category_options: updatedOptions,
      });

      // Reset states
      setOptionValue("");
      setEditingOption(null);
    } catch (err) {
      console.error("Option action error:", err);
      setError(
        `Failed to ${
          action === "add" ? "add" : action === "edit" ? "edit" : "delete"
        } option: ${err.message}`
      );
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Spravovať možnosti - {category.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex space-x-2">
              <Input
                value={optionValue}
                onChange={(e) => setOptionValue(e.target.value)}
                placeholder="Zadajte hodnotu možnosti"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && optionValue.trim()) {
                    e.preventDefault();
                    editingOption
                      ? handleOptionAction("edit")
                      : handleOptionAction("add");
                  }
                }}
              />
              {editingOption ? (
                <div className="flex space-x-2">
                  <Button onClick={() => handleOptionAction("edit")}>
                    <Save className="w-4 h-4 mr-2" />
                    Uložiť
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingOption(null);
                      setOptionValue("");
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Zrušiť
                  </Button>
                </div>
              ) : (
                <Button onClick={() => handleOptionAction("add")}>
                  <Plus className="w-4 h-4 mr-2" />
                  Pridať
                </Button>
              )}
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {category.custom_category_options?.map((option) => (
                <div
                  key={option.id}
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium">{option.value}</span>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingOption(option);
                        setOptionValue(option.value);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setOptionToDelete(option)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!optionToDelete}
        onOpenChange={() => setOptionToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vymazať možnosť</AlertDialogTitle>
            <AlertDialogDescription>
              Naozaj chcete vymazať túto možnosť? Táto akcia sa nedá vrátiť
              späť.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušiť</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleOptionAction("delete", optionToDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Vymazať
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Main Component
const CustomCategoriesManagement = () => {
  const supabase = useSupabase();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dialogState, setDialogState] = useState({ type: null, data: null });
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // Get the categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("custom_categories")
        .select("*")
        .eq("company_id", user.company_id)
        .eq("creator_id", user.id) // Add this line to filter by creator
        .eq("is_active", true);

      if (categoriesError) throw categoriesError;

      // For each category, get its options
      const categoriesWithOptions = await Promise.all(
        categoriesData.map(async (category) => {
          const { data: options, error: optionsError } = await supabase
            .from("custom_category_options")
            .select("*")
            .eq("category_id", category.id);

          if (optionsError) throw optionsError;

          return {
            ...category,
            custom_category_options: options || [],
          };
        })
      );

      setCategories(categoriesWithOptions || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryAction = async (action, data) => {
    try {
      let result;

      switch (action) {
        case "add":
          // Step 1: Insert the new category
          const { data: newCategory, error: addError } = await supabase
            .from("custom_categories")
            .insert([
              {
                ...data,
                company_id: user.company_id,
                creator_id: user.id, // Add this line to set creator
                is_active: true,
              },
            ])
            .select();

          if (addError) throw addError;

          // Step 2: Create a complete object with empty options array
          result = {
            ...newCategory[0],
            custom_category_options: [],
          };

          setCategories([result, ...categories]);
          break;

        case "edit":
          const { custom_category_options, ...categoryDataToUpdate } = data;

          // Step 1: Update the category with filtered data
          const { data: updatedCategory, error: updateError } = await supabase
            .from("custom_categories")
            .update(categoryDataToUpdate) // Use filtered data instead of raw data
            .eq("id", dialogState.data.id)
            .select();

          if (updateError) throw updateError;

          // Step 2: Get the options separately
          const { data: options, error: optionsError } = await supabase
            .from("custom_category_options")
            .select("*")
            .eq("category_id", dialogState.data.id);

          if (optionsError) throw optionsError;

          // Step 3: Manually combine them
          result = {
            ...updatedCategory[0],
            custom_category_options: options || [],
          };

          setCategories(
            categories.map((cat) =>
              cat.id === dialogState.data.id ? result : cat
            )
          );
          break;
        case "delete":
          result = await supabase
            .from("custom_categories")
            .update({ is_active: false })
            .eq("id", data.id);

          if (result.error) throw result.error;
          setCategories(categories.filter((cat) => cat.id !== data.id));
          break;
      }

      setDialogState({ type: null, data: null });
    } catch (err) {
      console.error("Category action error:", err);
      setError(
        `Failed to ${
          action === "add" ? "add" : action === "edit" ? "edit" : "delete"
        } category: ${err.message}`
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Vlastné kategórie</CardTitle>
            <CardDescription>
              Vytvorte a spravujte kategórie pre používateľov
            </CardDescription>
          </div>
          <Button onClick={() => setDialogState({ type: "add", data: null })}>
            <Plus className="w-4 h-4 mr-2" />
            Pridať kategóriu
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Názov</TableHead>
              <TableHead>Popis</TableHead>
              <TableHead>Možnosti</TableHead>
              <TableHead className="w-[100px]">Akcie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id}>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell>{category.description}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {category.custom_category_options?.length || 0} možností
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          setDialogState({ type: "edit", data: category })
                        }
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Upraviť
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setSelectedCategory(category)}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Spravovať možnosti
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          if (
                            window.confirm(
                              "Naozaj chcete vymazať túto kategóriu?"
                            )
                          ) {
                            handleCategoryAction("delete", category);
                          }
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Vymazať
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {(dialogState.type === "add" || dialogState.type === "edit") && (
          <CategoryFormDialog
            open={true}
            onClose={() => setDialogState({ type: null, data: null })}
            onSubmit={(data) => handleCategoryAction(dialogState.type, data)}
            initialData={dialogState.data}
          />
        )}

        {selectedCategory && (
          <OptionsManagement
            category={selectedCategory}
            onUpdate={(updatedCategory) => {
              // Update both states
              setCategories(
                categories.map((cat) =>
                  cat.id === updatedCategory.id ? updatedCategory : cat
                )
              );
              setSelectedCategory(updatedCategory); // Add this line
            }}
            onClose={() => setSelectedCategory(null)}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default CustomCategoriesManagement;
